import {
  DRAFT_APPROVAL_KIND,
  isSessionDraftToolName,
  resolveDraftPresentation,
} from '../../contracts/approval-kinds';
import type { ChatItem, ToolUseItem } from '../../contracts/harness.types';
import type { ViewedDocumentContext } from '../../contracts/projects/requests.types';
import { sessionDraftBeforeText } from '../../contracts/session-draft-before-text';
import { getPendingApprovalIdForItem } from '../../data/harness/approvals/get-pending-approval-id';
import type {
  DraftApprovalBlockProps,
  ResolveDraftApproval,
} from './draft-approval-block';

export type SessionDraftCard = {
  props: Omit<DraftApprovalBlockProps, 'renderBody'>;
  /** A known draft approval: the page may wrap it with redirects. */
  redirectsEligible: boolean;
};

/**
 * The draft card for a timeline item, or `null` when the item is not a
 * streaming or pending draft.
 *
 * Live SSE approvals omit `approval_kind` until the snapshot hydrates, so a
 * session-write tool is a draft by name meanwhile; otherwise `toBlockStatus`
 * maps pending_approval to running and the row spins forever. Redirects wait
 * for the kind, since the name fallback also matches `default` approvals.
 */
export const sessionDraftCard = (
  item: ToolUseItem,
  {
    items,
    viewedDocument,
    onResolve,
  }: {
    items?: ChatItem[];
    /** The on-screen document, with the session it belongs to. */
    viewedDocument?: ViewedDocumentContext & { session_id?: string };
    onResolve?: ResolveDraftApproval;
  }
): SessionDraftCard | null => {
  const pendingApprovalId = getPendingApprovalIdForItem(item);
  const kind = item.approval?.approval_kind;
  const isStreaming =
    item.status === 'streaming_input' && isSessionDraftToolName(item.name);
  const isPending =
    item.status === 'pending_approval' &&
    Boolean(pendingApprovalId) &&
    (kind === DRAFT_APPROVAL_KIND || (!kind && isSessionDraftToolName(item.name)));
  if (!isStreaming && !isPending) return null;

  const toolInput = (item.input as Record<string, unknown> | undefined) ?? {};
  const draft = resolveDraftPresentation(item.name, item.approval?.context?.draft);
  return {
    props: {
      toolName: item.name,
      toolInput,
      partialInput: item.partialInput,
      status: isStreaming ? 'streaming_input' : 'pending_approval',
      approvalId: isPending ? (pendingApprovalId ?? undefined) : undefined,
      draft,
      beforeText: sessionDraftBeforeText(items, item, viewedDocument),
      onResolve: isPending ? onResolve : undefined,
    },
    redirectsEligible: isPending && kind === DRAFT_APPROVAL_KIND,
  };
};
